/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  CHECK_SUGGESTION_STATUS,
  ErrorResponse,
  GET_IDEA_SUGGESTION,
  SUGGEST_IDEA,
  SuggestionResult,
} from '@frontend/data-access';
import {
  IdeaSuggestionList,
  SuggestionCard,
} from '@frontend/feature-idea-suggestion';
import { App, Button, Modal } from 'antd';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

interface Props {
  ideaId?: string;
}

export function IdeaDetailAISuggestion({ ideaId }: Props) {
  const { notification } = App.useApp();
  const router = useRouter();
  const { t: tcommon } = useTranslation('common');
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const id = ideaId ?? (router.query['id'] as string);

  const { data: suggestedChallenges, refetch: refetchSuggestedChallenges } =
    useQuery<{
      listSuggestionResults: {
        candidateResponses: SuggestionResult[];
        total: number;
      };
    }>(GET_IDEA_SUGGESTION, {
      fetchPolicy: 'network-only',
      variables: { id, limit: 100, page: 1, type: 'IDEA' },
      skip: !id,
    });

  const { data: status, refetch: refetchStatus } = useQuery<{
    checkSuggestionStatus: 'IN_PROGRESS' | 'NONE' | 'DONE';
  }>(CHECK_SUGGESTION_STATUS, {
    variables: { primaryId: id, primaryType: 'IDEA' },
    skip: !id,
    onCompleted(data) {
      if (data?.checkSuggestionStatus === 'DONE') {
        refetchSuggestedChallenges();
      }
    },
  });

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: tcommon('error'),
      description: tcommon('suggestion_not_available'),
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [
    suggestChallenges,
    { data: suggestChallengesData, loading: suggestChallengesLoading },
  ] = useMutation<{
    suggest: number;
  }>(SUGGEST_IDEA, {
    fetchPolicy: 'network-only',
    onError: handleError,
    onCompleted() {
      refetchStatus();
    },
  });

  const handleAIModal = () => {
    setAiModalOpen(!aiModalOpen);
  };

  useEffect(() => {
    if (status?.checkSuggestionStatus === 'IN_PROGRESS') {
      let timer: NodeJS.Timeout;
      // eslint-disable-next-line prefer-const
      timer = setInterval(() => {
        refetchStatus();
      }, 2000);
      return () => {
        clearInterval(timer);
      };
    }
    return;
  }, [refetchStatus, status]);

  const getSuggestionStatus = () => {
    if (
      status?.checkSuggestionStatus === 'IN_PROGRESS' ||
      suggestChallengesLoading
    ) {
      // Loading state
      return (
        <SuggestionCard>
          <div className="flex flex-row gap-2 items-center flex-wrap">
            <div className="flex-grow">
              <div>{tcommon('notify_complete')}</div>
            </div>
            <Button type="primary" shape="round" loading={true}>
              Starting
            </Button>
            <div></div>
          </div>
        </SuggestionCard>
      );
    } else if (
      status?.checkSuggestionStatus === 'DONE' &&
      (suggestedChallenges?.listSuggestionResults?.total ||
        suggestChallengesData?.suggest)
    ) {
      // Suggestion found
      return (
        <SuggestionCard>
          <div className="flex flex-row gap-2 items-center flex-wrap">
            <div className="flex-grow">
              <div className="mb-1">
                <span>{tcommon('we_have_found')} </span>
                <span className="bg-green-600 rounded-full text-white px-2 py-[2px] whitespace-nowrap">
                  {tcommon('total_challenges', {
                    total: suggestedChallenges?.listSuggestionResults?.total,
                  })}
                </span>
                <span> {tcommon('that_match_your_idea')}</span>
              </div>
            </div>
            <Button type="primary" shape="round" onClick={handleAIModal}>
              {tcommon('see_challenges')}
            </Button>
            <Button
              shape="round"
              loading={suggestChallengesLoading}
              onClick={() => {
                suggestChallenges({ variables: { id, type: 'IDEA' } });
              }}
            >
              {tcommon('retry')}
            </Button>
            <div></div>
          </div>
        </SuggestionCard>
      );
    } else if (
      suggestChallengesData?.suggest === 0 &&
      (status?.checkSuggestionStatus === 'DONE' ||
        !suggestedChallenges?.listSuggestionResults?.total)
    ) {
      // When there's no suggestion
      // Or the result is empty
      return (
        <SuggestionCard>
          <div className="flex flex-row gap-2 items-center flex-wrap">
            <div className="flex-grow">
              <div>{tcommon('look_for_challenges')}</div>
            </div>
            <Button
              type="primary"
              shape="round"
              onClick={() =>
                router.push('/innovation?explore=intra-challenges')
              }
            >
              {tcommon('go_to_discover')}
            </Button>
            <div></div>
          </div>
        </SuggestionCard>
      );
    } else {
      // User haven't started a suggestion
      return (
        <SuggestionCard>
          <div className="flex flex-row gap-2 items-center flex-wrap">
            <div className="flex-grow">
              <div>{tcommon('look_for_challenges')}</div>
            </div>
            <Button
              type="primary"
              shape="round"
              loading={suggestChallengesLoading}
              onClick={() => {
                suggestChallenges({ variables: { id, type: 'IDEA' } });
              }}
            >
              {tcommon('start_now')}
            </Button>
            <div></div>
          </div>
        </SuggestionCard>
      );
    }
  };

  return (
    <>
      {getSuggestionStatus()}

      <Modal
        title={tcommon('idea_detail_genai_suggestion_title')}
        open={aiModalOpen}
        onCancel={() => setAiModalOpen(false)}
        footer={null}
        width={1000}
        forceRender
      >
        {aiModalOpen && <IdeaSuggestionList />}
      </Modal>
    </>
  );
}

export default IdeaDetailAISuggestion;
